import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";

import { Table, Popconfirm, Form, Button, Space } from "antd";
import styled from "styled-components";
import { TableStyle } from "./SavedList/TableStyle";
import { PatientViewTableStyle } from "./SavedList/PatientViewTableStyle";
import { scrollTableToTop } from "../utils/misc";

const StyledTable = styled(Table)`
  ${TableStyle}
  ${PatientViewTableStyle}

  .ant-table {
    margin: 0;
    margin-bottom: 25px;
    max-height: ${(props) =>
      props.isFormOpen
        ? props.isNote
          ? `calc(100vh - 400px)`
          : `calc(100vh - 440px)`
        : props.isNote
        ? `calc(100vh - 320px)`
        : `calc(100vh - 400px)`};
  }

  .ant-table-empty .ant-table-tbody {
    height: ${(props) =>
      props.isFormOpen
        ? props.isNote
          ? `calc(100vh - 470px)`
          : `calc(100vh - 520px)`
        : props.isNote
        ? `calc(100vh - 390px)`
        : `calc(100vh - 455px)`};
  }
`;

const ReachableContext = React.createContext();

const EditableCell = ({ editing, inputNode, children, ...restProps }) => (
  <td {...restProps}>{editing ? inputNode : children}</td>
);

const EditableTableComponent = ({
  name,
  t,
  columns,
  setEdited,
  isActive,
  data,
  editAction = false,
  deleteAction = false,
  rowSelection = false,
  resetEditOnChange = true,
  isNote,
  isFormOpen,
  id
}) => {
  const [form] = Form.useForm();
  const [editingKey, setEditingKey] = useState("");
  const [hoveredRow, setHoveredRow] = useState(null);

  useEffect(() => {
    if (resetEditOnChange) {
      setEditingKey("");
    }
  }, [data, resetEditOnChange]);

  const mergedColumns = columns.map((col) => {
    const onCellFunc = col.onCell;
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        ...(onCellFunc && onCellFunc(record)),
        record,
        inputNode: col.inputNode(record),
        title: col.title,
        editing: isEditing(record)
      }),
      inputNode: undefined
    };
  });
  if (isActive) {
    if (editAction) {
      mergedColumns.push({
        dataIndex: "operation",
        width: 180,
        align: "end",
        render: (_, record, idx) => {
          const editable = isEditing(record);
          return editable ? (
            <Space>
              <Button id={`${name}-cell-edit-save-${idx}`} onClick={() => save(record)} style={{ marginRight: 8 }}>
                {t("save")}
              </Button>
              <Popconfirm title={t("sure_to_cancel")} onConfirm={cancel}>
                <Button id={`${name}-cell-edit-cancel-${idx}`}>{t("cancel")}</Button>
              </Popconfirm>
            </Space>
          ) : hoveredRow === record.key ? (
            <EditOutlined
              style={{ color: "#2B56A3", fontSize: 16 }}
              id={`${name}-cell-edit-btn-${idx}`}
              disabled={editingKey !== "" || !isActive}
              onClick={() => edit(record)}
            />
          ) : null;
        }
      });
    }
    if (deleteAction) {
      mergedColumns.push({
        dataIndex: "delete",
        width: 80,
        align: "center",
        render: (text, record, idx) =>
          hoveredRow === record.key || isEditing(record) ? (
            <DeleteOutlined
              id={`${name}-cell-delete-btn-${idx}`}
              style={{ color: "#2B56A3", fontSize: 16 }}
              onClick={() => {
                setEditingKey("");
                deleteAction(record);
              }}
            />
          ) : null
      });
    }
  }

  const isEditing = (record) => record.key === editingKey;
  const edit = (record) => {
    setEdited(true);
    form.setFieldsValue({ ...record });
    setEditingKey(record.key);
  };

  const cancel = () => {
    setEdited(false);
    setEditingKey("");
    form.resetFields();
  };

  const save = async (record) => {
    try {
      const values = await form.validateFields();
      setEditingKey("");
      setEdited(false);
      editAction({ ...record, ...values });
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
    }
  };

  const onTableChange = () => {
    scrollTableToTop(id);
  };

  return (
    <ReachableContext.Provider value={null}>
      <Form form={form} component={false}>
        <StyledTable
          id={id}
          components={{
            body: {
              cell: EditableCell
            }
          }}
          dataSource={data}
          columns={mergedColumns}
          isFormOpen={isFormOpen}
          rowClassName="editable-row"
          rowSelection={rowSelection}
          pagination={false}
          isNote={isNote}
          onChange={onTableChange}
          onRow={(record) => {
            return {
              onMouseEnter: () => {
                setHoveredRow(record.key);
              },
              onMouseLeave: () => {
                setHoveredRow(null);
              }
            };
          }}
        />
      </Form>
    </ReachableContext.Provider>
  );
};

const EditableTable = withTranslation()(EditableTableComponent);

export { EditableTable };
